"use client";

import { t } from "@/lib/i18n/interpolate";
import { formatCodePoint } from "@/lib/tool-kit/text/code-points";
import {
  type DisplaySegment,
  type HiddenFinding,
  tagToAscii,
} from "@/lib/tool-kit/text/hidden-characters";

/** What the view needs to say about a marker; every word comes from the widget. */
export interface RevealedTextStrings {
  /** Accessible name of the whole region. */
  label: string;
  /** "{name} — looks like {target}" */
  lookalikeTitle: string;
  /** "Tag characters spelling “{text}”" */
  tagRunTitle: string;
  /** Category names, shown when a character has no Unicode name of its own. */
  categoryName: (finding: HiddenFinding) => string;
}

const SUSPICIOUS =
  "mx-px rounded border border-amber-500/60 bg-amber-100 px-1 font-mono text-[11px] text-amber-950 dark:bg-amber-400/20 dark:text-amber-100";
const EXPECTED =
  "mx-px rounded border border-border bg-muted px-1 font-mono text-[11px] text-muted-foreground";
const LOOKALIKE =
  "rounded bg-rose-200/80 px-0.5 text-foreground underline decoration-rose-600 decoration-wavy dark:bg-rose-400/30";

function markerTitle(
  finding: HiddenFinding,
  strings: RevealedTextStrings
): string {
  const name = finding.name ?? strings.categoryName(finding);
  if (finding.category === "lookalike" && finding.replacement) {
    return `${formatCodePoint(finding.codePoint)} ${t(strings.lookalikeTitle, {
      name,
      target: finding.replacement,
    })}`;
  }
  return `${formatCodePoint(finding.codePoint)} ${name}`;
}

function Marker({
  findings,
  strings,
}: {
  findings: HiddenFinding[];
  strings: RevealedTextStrings;
}) {
  const [first] = findings;
  if (first.category === "lookalike") {
    return (
      <mark className={LOOKALIKE} title={markerTitle(first, strings)}>
        {first.char}
      </mark>
    );
  }
  const className = first.expected ? EXPECTED : SUSPICIOUS;
  if (findings.length > 1) {
    const text = findings.map((f) => tagToAscii(f.codePoint)).join("");
    return (
      <mark className={className} title={t(strings.tagRunTitle, { text })}>
        {`TAG «${text}»`}
      </mark>
    );
  }
  return (
    <mark className={className} title={markerTitle(first, strings)}>
      {first.label}
    </mark>
  );
}

/**
 * The reader's text with every hidden character drawn as a labeled marker, in the
 * order the characters are stored. Because bidirectional controls are replaced by their
 * markers, text that an override displayed backwards appears here as it really is.
 */
export function RevealedText({
  segments,
  strings,
}: {
  segments: readonly DisplaySegment[];
  strings: RevealedTextStrings;
}) {
  return (
    <section
      aria-label={strings.label}
      className="max-h-[480px] overflow-y-auto whitespace-pre-wrap break-words rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed [unicode-bidi:plaintext]"
    >
      {segments.map((segment) =>
        segment.kind === "text" ? (
          <span key={segment.start}>{segment.text}</span>
        ) : (
          <Marker
            findings={segment.findings}
            key={segment.start}
            strings={strings}
          />
        )
      )}
    </section>
  );
}
